import React, {useMemo, useState} from 'react'
import axios from 'axios'
import {UserPlus, Upload, X, Briefcase, Phone, Mail, FileText} from 'lucide-react'
import {useQuery} from '@tanstack/react-query'
import {QUERIES, stringifyRequestQuery} from '../../../../../_metronic/helpers'
import {v2_get_request_api} from '../../../../../api/get'
import {Job} from '../../job/jobs-list/core/_models'
import {fileUpload} from '../../../../../_component/fileUtils'
import {toast} from 'react-toastify'

type Props = {
  show: boolean
  handleClose: () => void
  onSuccess: () => void
  initialJobId?: string | null
}

interface AdditionalField {
  id: string;
  name: string;
  type: 'text' | 'textarea' | 'select' | 'switch' | 'number';
  required: boolean;
  options?: string;
}

const getBaseUrl = () => {
    let url = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
    if (url === 'http://localhost:3000') url = 'http://localhost:3000/api';
    return url;
}
const API_URL = getBaseUrl() + '/application'

export const CreateApplicationModal: React.FC<Props> = ({show, handleClose, onSuccess, initialJobId}) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    job_id: initialJobId || '',
    cover_letter: '',
  })

  const [answers, setAnswers] = useState<Record<string, any>>({})
  const [selectedJob, setSelectedJob] = useState<any>(null)

  React.useEffect(() => {
    if (show) {
      setFormData(prev => ({...prev, job_id: initialJobId || ''}))
    }
  }, [show, initialJobId])

  // Fetch full job details when job_id changes
  React.useEffect(() => {
    const fetchJobDetails = async () => {
      if (!formData.job_id) {
        setSelectedJob(null)
        setAnswers({})
        return
      }

      try {
        const response = await axios.get(`${getBaseUrl()}/job/${formData.job_id}`)
        
        const job = response?.data?.data || response?.data || response
        setSelectedJob(job)
        
        // Initialize blank answers for additional fields
        const initialAnswers: Record<string, any> = {}
        job?.additional_fields?.forEach((field: AdditionalField) => {
          initialAnswers[field.name] = ''
        })
        setAnswers(initialAnswers)
      } catch (err) {
        console.error('Failed to fetch job details:', err)
      }
    }

    fetchJobDetails()
  }, [formData.job_id])

  const [resume, setResume] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const jobsQuery = useMemo(
    () =>
      stringifyRequestQuery({
        page_number: 1,
        page_size: 200,
        sort_field: 'title',
        sort_order: 'asc',
      } as any),
    []
  )

  const {data: jobsResponse} = useQuery([QUERIES.JOB_LIST, jobsQuery], v2_get_request_api)

  const jobOptions = useMemo(() => {
    const entities: Job[] = jobsResponse?.data?.entities || jobsResponse?.entities || []
    return (entities || [])
      .filter((j) => j?.id && j?.title)
      .map((j) => ({value: String(j.id), label: String(j.title).trim()}))
  }, [jobsResponse])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {name, value} = e.target
    setFormData((prev) => ({...prev, [name]: value}))
  }

  const handleAnswerChange = (fieldName: string, value: any) => {
    setAnswers(prev => ({...prev, [fieldName]: value}))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setResume(e.target.files[0])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      let resume_url = ''
      if (resume) {
        resume_url = await fileUpload(resume, 'resumes')
      }

      if (!resume_url && resume) {
        throw new Error('Failed to upload resume')
      }

      await axios.post(API_URL, {
        ...formData,
        answers: JSON.stringify(answers),
        resume_url,
        status: 'applied'
      })

      toast.success('Applicant created successfully')
      onSuccess()
      handleClose()
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        job_id: '',
        cover_letter: '',
      })
      setResume(null)
      setAnswers({})
      setSelectedJob(null)
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create applicant')
    } finally {
      setLoading(false)
    }
  }

  if (!show) return null

  return (
    <div className='fixed inset-0 z-[1050] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 md:p-8'>
      <div className='bg-white w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200'>
        <div className='bg-[#008080] text-white py-5 px-8 relative flex-shrink-0'>
          <div className='flex items-center gap-4'>
            <div className='w-12 h-12 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-md'>
              <UserPlus size={24} />
            </div>
            <div>
              <h3 className='text-xl font-bold mb-0'>Add New Applicant</h3>
              <p className='text-white/70 text-sm mb-0'>Personalize and submit candidate information</p>
            </div>
          </div>
          <button
            type='button'
            className='absolute top-6 right-6 text-white/70 hover:text-white transition-colors'
            onClick={handleClose}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className='flex flex-col flex-1 min-h-0 bg-white'>
          <div className='modal-body p-8 overflow-y-auto flex-1 custom-scrollbar'>
            {error && (
              <div className='alert alert-danger d-flex align-items-center border-0 mb-8 bg-red-50 text-red-700 p-5 rounded-xl text-sm font-semibold shadow-sm'>
                <div className='w-2 h-8 bg-red-500 rounded-full mr-4'></div>
                {error}
              </div>
            )}

            <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-8'>
              <div className='space-y-2'>
                <label className='block text-xs font-black text-gray-400 uppercase tracking-widest'>Full Name</label>
                <div className='relative group'>
                  <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#008080] transition-colors'>
                    <FileText size={18} className='text-gray-400 group-focus-within:text-[#008080]' />
                  </div>
                  <input
                    type='text'
                    name='full_name'
                    className='block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-[#008080]/20 focus:border-[#008080] focus:bg-white transition-all outline-none font-medium'
                    placeholder='e.g. Johnathan Doe'
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className='space-y-2'>
                <label className='block text-xs font-black text-gray-400 uppercase tracking-widest'>Email Address</label>
                <div className='relative group'>
                  <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#008080] transition-colors'>
                    <Mail size={18} className='text-gray-400 group-focus-within:text-[#008080]' />
                  </div>
                  <input
                    type='email'
                    name='email'
                    className='block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-[#008080]/20 focus:border-[#008080] focus:bg-white transition-all outline-none font-medium'
                    placeholder='john@company.com'
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-8'>
              <div className='space-y-2'>
                <label className='block text-xs font-black text-gray-400 uppercase tracking-widest'>Phone Number</label>
                <div className='relative group'>
                  <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#008080] transition-colors'>
                    <Phone size={18} className='text-gray-400 group-focus-within:text-[#008080]' />
                  </div>
                  <input
                    type='tel'
                    name='phone'
                    className='block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-[#008080]/20 focus:border-[#008080] focus:bg-white transition-all outline-none font-medium'
                    placeholder='+1 (555) 000-0000'
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className='space-y-2'>
                <label className='block text-xs font-black text-gray-400 uppercase tracking-widest'>Position</label>
                <div className='relative group'>
                  <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#008080] transition-colors'>
                    <Briefcase size={18} className='text-gray-400 group-focus-within:text-[#008080]' />
                  </div>
                  {initialJobId ? (
                    <div className='block w-full pl-11 pr-4 py-3.5 bg-gray-100 border border-gray-200 rounded-xl text-gray-700 text-sm font-bold truncate'>
                      {selectedJob?.title || jobOptions.find(opt => opt.value === initialJobId)?.label || 'Loading...'}
                    </div>
                  ) : (
                    <select
                      name='job_id'
                      className='block w-full pl-11 pr-10 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-[#008080]/20 focus:border-[#008080] focus:bg-white transition-all outline-none font-medium appearance-none'
                      value={formData.job_id}
                      onChange={handleChange}
                      required
                    >
                      <option value=''>Select a position...</option>
                      {jobOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {!initialJobId && (
                    <div className='absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-400'>
                      <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7' />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Dynamic Additional Fields */}
            {selectedJob?.additional_fields && selectedJob.additional_fields.length > 0 && (
              <div className='mb-8 pt-6 border-t border-gray-100'>
                <h4 className='text-xs font-black text-[#008080] uppercase tracking-widest mb-6'>Additional Questions</h4>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
                  {selectedJob.additional_fields.map((field: AdditionalField) => (
                    <div 
                      key={field.name} 
                      className={`space-y-2 ${field.type === 'textarea' ? 'md:col-span-2' : ''}`}
                    >
                      <label className='block text-sm font-bold text-gray-700'>
                        {field.name} {field.required && <span className='text-red-500'>*</span>}
                      </label>
                      {field.type === 'select' ? (
                        <select
                          className='block w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-[#008080]/20 focus:border-[#008080] focus:bg-white transition-all outline-none font-medium appearance-none'
                          value={answers[field.name] || ''}
                          onChange={(e) => handleAnswerChange(field.name, e.target.value)}
                          required={field.required}
                        >
                          <option value=''>Select an option</option>
                          {field.options?.split(',').map(opt => (
                            <option key={opt.trim()} value={opt.trim()}>{opt.trim()}</option>
                          ))}
                        </select>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          className='block w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-[#008080]/20 focus:border-[#008080] focus:bg-white transition-all outline-none font-medium min-h-[100px] resize-none'
                          value={answers[field.name] || ''}
                          onChange={(e) => handleAnswerChange(field.name, e.target.value)}
                          required={field.required}
                        />
                      ) : field.type === 'switch' ? (
                        <div className='flex items-center gap-6 py-2'>
                          <label className='flex items-center gap-2 cursor-pointer'>
                            <input
                              type='radio'
                              name={field.name}
                              className='w-4 h-4 text-[#008080] focus:ring-[#008080]/20'
                              checked={answers[field.name] === true}
                              onChange={() => handleAnswerChange(field.name, true)}
                              required={field.required}
                            />
                            <span className='text-sm text-gray-700 font-medium'>Yes</span>
                          </label>
                          <label className='flex items-center gap-2 cursor-pointer'>
                            <input
                              type='radio'
                              name={field.name}
                              className='w-4 h-4 text-[#008080] focus:ring-[#008080]/20'
                              checked={answers[field.name] === false}
                              onChange={() => handleAnswerChange(field.name, false)}
                              required={field.required}
                            />
                            <span className='text-sm text-gray-700 font-medium'>No</span>
                          </label>
                        </div>
                      ) : (
                        <input
                          type={field.type === 'number' ? 'number' : 'text'}
                          className='block w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-[#008080]/20 focus:border-[#008080] focus:bg-white transition-all outline-none font-medium'
                          value={answers[field.name] || ''}
                          onChange={(e) => handleAnswerChange(field.name, e.target.value)}
                          required={field.required}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className='mb-8'>
              <label className='block text-xs font-black text-gray-400 uppercase tracking-widest mb-3'>Cover Letter (Personal Note)</label>
              <textarea
                name='cover_letter'
                className='block w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-[#008080]/20 focus:border-[#008080] focus:bg-white transition-all outline-none font-medium min-h-[140px] resize-none'
                placeholder='Share some details about why you are a great fit for this role...'
                value={formData.cover_letter}
                onChange={handleChange}
              ></textarea>
            </div>

            <div className='mb-0'>
              <label className='block text-xs font-black text-gray-400 uppercase tracking-widest mb-3'>Resume / Curriculum Vitae</label>
              <div 
                className={`
                  relative border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all duration-300
                  ${resume ? 'border-[#008080] bg-[#008080]/5' : 'border-gray-200 bg-gray-50/50 hover:bg-gray-100/50 hover:border-[#008080]/50'}
                `}
                onClick={() => document.getElementById('resume-upload')?.click()}
              >
                <input
                  id='resume-upload'
                  type='file'
                  className='hidden'
                  accept='.pdf,.doc,.docx'
                  onChange={handleFileChange}
                />
                <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center transition-transform duration-300 ${resume ? 'bg-[#008080] text-white scale-110 shadow-lg shadow-teal-500/20' : 'bg-white text-gray-400 shadow-sm'}`}>
                  <Upload size={28} />
                </div>
                {resume ? (
                  <div className='space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300'>
                    <p className='text-sm font-bold text-gray-900'>{resume.name}</p>
                    <p className='text-xs text-teal-600 font-semibold uppercase tracking-wider'>File uploaded successfully</p>
                    <button 
                      type='button' 
                      className='mt-2 text-xs font-bold text-red-500 hover:text-red-600 flex items-center gap-1 mx-auto transition-colors'
                      onClick={(e) => {
                        e.stopPropagation()
                        setResume(null)
                      }}
                    >
                      <X size={14} /> Remove file
                    </button>
                  </div>
                ) : (
                  <div>
                    <h4 className='text-sm font-bold text-gray-900 mb-1'>Drop your file here or click to browse</h4>
                    <p className='text-xs text-gray-500 mb-0'>Supports PDF, DOC, DOCX • Max size 10MB</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className='modal-footer border-t border-gray-100 p-8 flex justify-end gap-4 bg-gray-50/30 flex-shrink-0'>
            <button
              type='button'
              className='px-8 py-3.5 bg-white border border-gray-200 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-100 hover:border-gray-300 transition-all duration-200 shadow-sm'
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='px-8 py-3.5 bg-[#008080] text-white text-sm font-black rounded-xl hover:bg-[#006666] transition-all duration-200 flex items-center gap-3 shadow-xl shadow-teal-500/20 disabled:opacity-75 disabled:cursor-not-allowed group'
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                  Processing...
                </>
              ) : (
                <>
                  <UserPlus size={18} className='group-hover:scale-110 transition-transform duration-200' />
                  Add Applicant
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
